import React, { useEffect, useMemo, useState } from "react";
import {
	Alert,
	Box,
	Button,
	Checkbox,
	CircularProgress,
	FormControl,
	FormControlLabel,
	FormGroup,
	Grid,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	Typography,
	Chip,
	Accordion,
	AccordionSummary,
	AccordionDetails,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import RefreshIcon from "@mui/icons-material/Refresh";
import ScheduleIcon from "@mui/icons-material/Schedule";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";
import BackspaceIcon from "@mui/icons-material/Backspace";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import WorkHistoryIcon from "@mui/icons-material/WorkHistory";
import { useNavigate } from "react-router-dom";
import {
	guardarPreparacionExportacionFichajes,
	getPreparacionExportacionFichajes,
	getConvenios,
	updateParteGrupoEstado,
} from "../../../services/apis";
import { SubHeader } from "../../../utils/componentes-utiles/SubHeader";
import {
	formatDateTimeEs,
	formatScoreOverHundred,
} from "../../../utils/formatters";
import { Convenios } from '../../rrhh/Convenios';


const getTodayYMD = () => {
	const now = new Date();

	return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(
		now.getDate()
	).padStart(2, "0")}`;
};

const normalizeTimeValue = (value) => {
	const normalized = String(value ?? "").trim();
	const match = normalized.match(/^(2[0-3]|[01]\d):([0-5]\d)(?::([0-5]\d))?$/);

	if (!match) {
		return "";
	}

	return `${match[1]}:${match[2]}`;
};

const addHoursToTime = (time, hours) => {
	const normalizedTime = normalizeTimeValue(time);
	const numericHours = Number(hours);

	if (!normalizedTime || !Number.isFinite(numericHours) || numericHours <= 0) {
		return "";
	}

	const [hh, mm] = normalizedTime.split(":").map(Number);
	const startMinutes = hh * 60 + mm;
	const extraMinutes = Math.round(numericHours * 60);
	const totalMinutes = startMinutes + extraMinutes;

	const outHours = Math.floor(totalMinutes / 60) % 24;
	const outMinutes = totalMinutes % 60;

	return `${String(outHours).padStart(2, "0")}:${String(outMinutes).padStart(2, "0")}`;
};

const rowKeyFromItem = (item) => `${item?.id_cuadrilla ?? 0}-${item?.id_usuario ?? 0}`;

const buildHoursMap = (items, fallbackHour, previous = {}) => {
	const next = {};

	items.forEach((item) => {
		const key = rowKeyFromItem(item);

		next[key] =
			normalizeTimeValue(previous[key]) ||
			normalizeTimeValue(item?.hora_entrada_preparada) ||
			fallbackHour;
	});

	return next;
};

const buildExitHoursMap = (items, fallbackHour, previousEntrada = {}, previousSalida = {}) => {
	const next = {};

	items.forEach((item) => {
		const key = rowKeyFromItem(item);

		const entrada =
			normalizeTimeValue(previousEntrada[key]) ||
			normalizeTimeValue(item?.hora_entrada_preparada) ||
			fallbackHour;

		next[key] =
			normalizeTimeValue(previousSalida[key]) ||
			normalizeTimeValue(item?.hora_salida_preparada) ||
			addHoursToTime(entrada, item?.horas_convenio);
	});

	return next;
};

const buildSelectionMapFromRows = (items, predicate) => {
	const next = {};

	items.forEach((item) => {
		if (predicate(item)) {
			next[rowKeyFromItem(item)] = true;
		}
	});

	return next;
};

const getEstadoColor = (item) => (Number(item?.exportado ?? 0) === 1 ? "success" : "warning");

export const PrepararExportacionFichajes = () => {
	const navigate = useNavigate();
	const today = useMemo(() => getTodayYMD(), []);

	const [fechaDesde, setFechaDesde] = useState(today);
	const [fechaHasta, setFechaHasta] = useState(today);
	const [search, setSearch] = useState("");

	const [soloPendientes] = useState(false);

	const [filtrosEstado, setFiltrosEstado] = useState({
		validado: true,
		revisado: true,
	});

	const [filtrosPreparacion, setFiltrosPreparacion] = useState({
		pendientes: true,
		preparados: true,
	});

	const [horaBase, setHoraBase] = useState("08:00");
	const [rows, setRows] = useState([]);
	const [hoursByRow, setHoursByRow] = useState({});
	const [exitHoursByRow, setExitHoursByRow] = useState({});
	const [horaBaseByGroup, setHoraBaseByGroup] = useState({});
	const [selectedRows, setSelectedRows] = useState({});
	const [resumen, setResumen] = useState({
		total: 0,
		pendientes: 0,
		preparados: 0,
		cuadrillas: 0,
	});
	const [loaded, setLoaded] = useState(false);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState("");
	const [message, setMessage] = useState("");
	const [accessDenied, setAccessDenied] = useState(false);

	const [openConvenios, setOpenConvenios] = useState(false);
	const [convenios, setConvenios] = useState([]);
	const [conveniosLoading, setConveniosLoading] = useState(false);
	const [convenioByGroup, setConvenioByGroup] = useState({});
	const [savingConvenioByGroup, setSavingConvenioByGroup] = useState({});

	const handleFiltroEstadoChange = (campo) => {
		setFiltrosEstado((current) => ({
			...current,
			[campo]: !current[campo],
		}));
	};

	const handleFiltroPreparacionChange = (campo) => {
		setFiltrosPreparacion((current) => ({
			...current,
			[campo]: !current[campo],
		}));
	};

	const visibleRows = useMemo(() => {
		return rows.filter((row) => {
			const preparado = Number(row?.exportado ?? 0) === 1;

			if (preparado && !filtrosPreparacion.preparados) {
				return false;
			}

			if (!preparado && !filtrosPreparacion.pendientes) {
				return false;
			}

			return true;
		});
	}, [rows, filtrosPreparacion]);

	const selectedCount = useMemo(
		() => visibleRows.filter((row) => selectedRows[rowKeyFromItem(row)]).length,
		[visibleRows, selectedRows]
	);

	const groupedRows = useMemo(() => {
		const groups = {};

		visibleRows.forEach((row) => {
			const cuadrillaId = row.id_cuadrilla;

			if (!groups[cuadrillaId]) {
				groups[cuadrillaId] = {
					cuadrilla: {
						id: cuadrillaId,
						nombre: row.nombre_cuadrilla,
						fecha: row.fecha_cuadrilla,
						cliente: row.nombre_cliente,
						cabo: row.cabo_completo,
						nif_cabo: row.nif_cabo,
						id_convenio: row.id_convenio ?? null,
						nombre_convenio: row.nombre_convenio ?? null,
						horas_convenio: row.horas_convenio ?? null,
					},
					fichajes: [],
				};
			}

			groups[cuadrillaId].fichajes.push(row);
		});

		return Object.values(groups);
	}, [visibleRows]);

	const allVisibleSelected = useMemo(() => {
		if (visibleRows.length === 0) return false;

		return visibleRows.every((row) => selectedRows[rowKeyFromItem(row)]);
	}, [visibleRows, selectedRows]);

	const resumenVisible = useMemo(() => {
		const total = visibleRows.length;
		const preparados = visibleRows.filter((item) => Number(item?.exportado ?? 0) === 1).length;
		const pendientes = total - preparados;
		const cuadrillas = new Set(visibleRows.map((item) => item.id_cuadrilla)).size;

		return {
			total,
			pendientes,
			preparados,
			cuadrillas,
		};
	}, [visibleRows]);

	const cargarConvenios = async () => {
		setConveniosLoading(true);

		try {
			const response = await getConvenios();

			if (response?.success) {
				setConvenios(Array.isArray(response.data) ? response.data : []);
			} else {
				setConvenios([]);
			}
		} catch (err) {
			console.error("Error cargando convenios:", err);
			setConvenios([]);
		} finally {
			setConveniosLoading(false);
		}
	};

	const cargarDatos = async ({ preserveMessage = false } = {}) => {
		setLoaded(false);
		setError("");

		if (!preserveMessage) {
			setMessage("");
		}

		setAccessDenied(false);

		if (!fechaDesde || !fechaHasta) {
			setRows([]);
			setResumen({ total: 0, pendientes: 0, preparados: 0, cuadrillas: 0 });
			setError("Debes indicar ambas fechas.");
			setLoaded(true);
			return;
		}

		if (fechaDesde > fechaHasta) {
			setRows([]);
			setResumen({ total: 0, pendientes: 0, preparados: 0, cuadrillas: 0 });
			setError("La fecha desde no puede ser mayor que la fecha hasta.");
			setLoaded(true);
			return;
		}

		try {
			const response = await getPreparacionExportacionFichajes({
				fechaDesde,
				fechaHasta,
				soloPendientes: false,
				search,
				filtrosEstado,
			});

			if (response?.success) {
				const data = Array.isArray(response.data) ? response.data : [];
				const resumenResponse = response.resumen || {};

				const nextConvenioByGroup = {};

				data.forEach((item) => {
					const idCuadrilla = item?.id_cuadrilla;

					if (!idCuadrilla) return;

					if (nextConvenioByGroup[idCuadrilla] === undefined) {
						nextConvenioByGroup[idCuadrilla] =
							item?.id_convenio !== null && item?.id_convenio !== undefined
								? String(item.id_convenio)
								: "";
					}
				});

				setConvenioByGroup(nextConvenioByGroup);
				setRows(data);
				setResumen({
					total: Number(resumenResponse.total ?? 0),
					pendientes: Number(resumenResponse.pendientes ?? 0),
					preparados: Number(resumenResponse.preparados ?? 0),
					cuadrillas: Number(resumenResponse.cuadrillas ?? 0),
				});

				setHoursByRow((current) => {
					const nextEntrada = buildHoursMap(data, horaBase, current);

					setExitHoursByRow((currentSalida) =>
						buildExitHoursMap(data, horaBase, nextEntrada, currentSalida)
					);

					return nextEntrada;
				});

				setSelectedRows((current) => {
					const next = {};

					data.forEach((item) => {
						const key = rowKeyFromItem(item);

						if (current[key]) {
							next[key] = true;
						}
					});

					return next;
				});
			} else {
				setRows([]);
				setResumen({ total: 0, pendientes: 0, preparados: 0, cuadrillas: 0 });
				setError(response?.message || "No se pudo cargar la preparacion.");
			}
		} catch (err) {
			const status = err?.response?.status;

			if (status === 403) {
				setAccessDenied(true);
				setRows([]);
				setResumen({ total: 0, pendientes: 0, preparados: 0, cuadrillas: 0 });
				setError("Solo los roles 986 y 901 pueden acceder a este panel.");
			} else {
				console.error("Error cargando preparacion de exportacion:", err);
				setRows([]);
				setResumen({ total: 0, pendientes: 0, preparados: 0, cuadrillas: 0 });
				setError("No se pudo cargar la preparacion de exportacion.");
			}
		} finally {
			setLoaded(true);
		}
	};

	useEffect(() => {
		cargarDatos();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		cargarConvenios();
	}, []);

	const toggleRow = (item) => {
		const key = rowKeyFromItem(item);

		setSelectedRows((current) => ({
			...current,
			[key]: !current[key],
		}));
	};

	const toggleAllVisible = () => {
		setSelectedRows((current) => {
			const next = { ...current };

			visibleRows.forEach((item) => {
				const key = rowKeyFromItem(item);
				next[key] = !allVisibleSelected;
			});

			return next;
		});
	};

	const seleccionarPendientes = () => {
		setSelectedRows(
			buildSelectionMapFromRows(
				visibleRows,
				(item) => Number(item?.exportado ?? 0) !== 1
			)
		);
	};

	const limpiarSeleccion = () => {
		setSelectedRows({});
	};

	const toggleAllInGroup = (group) => {
		const allInGroupSelected = group.fichajes.every(
			(item) => selectedRows[rowKeyFromItem(item)]
		);

		setSelectedRows((current) => {
			const next = { ...current };

			group.fichajes.forEach((item) => {
				const key = rowKeyFromItem(item);
				next[key] = !allInGroupSelected;
			});

			return next;
		});
	};

	const applyHourBaseToGroup = (group) => {
		const cuadrillaId = group.cuadrilla.id;
		const baseHour = normalizeTimeValue(horaBaseByGroup[cuadrillaId] || horaBase);

		if (!baseHour) {
			setError("La hora base para esta cuadrilla no es valida.");
			return;
		}

		setError("");

		setHoursByRow((current) => {
			const nextEntrada = { ...current };
			const nextSalida = { ...exitHoursByRow };

			group.fichajes.forEach((item) => {
				const key = rowKeyFromItem(item);
				nextEntrada[key] = baseHour;
				nextSalida[key] = addHoursToTime(baseHour, item?.horas_convenio);
			});

			setExitHoursByRow(nextSalida);

			return nextEntrada;
		});
	};

	const aplicarHoraBase = () => {
		if (!selectedCount) {
			setError("Selecciona al menos un fichaje para aplicar la hora.");
			return;
		}

		const normalizedBaseHour = normalizeTimeValue(horaBase);

		if (!normalizedBaseHour) {
			setError("La hora base no es valida.");
			return;
		}

		setError("");

		setHoursByRow((current) => {
			const nextEntrada = { ...current };
			const nextSalida = { ...exitHoursByRow };

			visibleRows.forEach((item) => {
				const key = rowKeyFromItem(item);

				if (selectedRows[key]) {
					nextEntrada[key] = normalizedBaseHour;
					nextSalida[key] = addHoursToTime(normalizedBaseHour, item?.horas_convenio);
				}
			});

			setExitHoursByRow(nextSalida);

			return nextEntrada;
		});
	};

	const handleHoraEntradaChange = (item, value) => {
		const key = rowKeyFromItem(item);
		const entrada = normalizeTimeValue(value);

		setHoursByRow((current) => ({
			...current,
			[key]: entrada,
		}));

		setExitHoursByRow((current) => ({
			...current,
			[key]: addHoursToTime(entrada, item?.horas_convenio),
		}));
	};

	const handleHoraSalidaChange = (item, value) => {
		const key = rowKeyFromItem(item);

		setExitHoursByRow((current) => ({
			...current,
			[key]: normalizeTimeValue(value),
		}));
	};

	const handleConvenioParteChange = async (idCuadrilla, value) => {
		const nextValue = value === "" ? null : Number(value);

		setConvenioByGroup((current) => ({
			...current,
			[idCuadrilla]: value,
		}));

		setSavingConvenioByGroup((current) => ({
			...current,
			[idCuadrilla]: true,
		}));

		setError("");
		setMessage("");

		try {
			const response = await updateParteGrupoEstado({
				id_cuadrilla: idCuadrilla,
				id_convenio: nextValue,
			});

			if (response?.success === false) {
				setError(response?.message || "No se pudo actualizar el convenio del parte.");
				return;
			}

			setMessage("Convenio del parte actualizado correctamente.");
			await cargarDatos({ preserveMessage: true });
		} catch (err) {
			console.error("Error actualizando convenio del parte:", err);
			setError("No se pudo actualizar el convenio del parte.");
		} finally {
			setSavingConvenioByGroup((current) => ({
				...current,
				[idCuadrilla]: false,
			}));
		}
	};

	const guardarSeleccion = async (action) => {
		const selectedItems = visibleRows.filter((item) => selectedRows[rowKeyFromItem(item)]);

		if (selectedItems.length === 0) {
			setError("Selecciona al menos un fichaje.");
			return;
		}

		const payloadItems =
			action === "prepare"
				? selectedItems.map((item) => {
						const key = rowKeyFromItem(item);
						const horaEntrada =
							normalizeTimeValue(hoursByRow[key]) ||
							normalizeTimeValue(item?.hora_entrada_preparada) ||
							normalizeTimeValue(horaBase);

						const horaSalida =
							normalizeTimeValue(exitHoursByRow[key]) ||
							normalizeTimeValue(item?.hora_salida_preparada) ||
							addHoursToTime(horaEntrada, item?.horas_convenio);

						return {
							id_cuadrilla: item.id_cuadrilla,
							id_usuario: item.id_usuario,
							hora_entrada: horaEntrada,
							hora_salida: horaSalida,
						};
				  })
				: selectedItems.map((item) => ({
						id_cuadrilla: item.id_cuadrilla,
						id_usuario: item.id_usuario,
				  }));

		if (
			action === "prepare" &&
			payloadItems.some((item) => !normalizeTimeValue(item.hora_entrada))
		) {
			setError("Hay fichajes seleccionados sin una hora de entrada valida.");
			return;
		}

		if (
			action === "prepare" &&
			payloadItems.some((item) => !normalizeTimeValue(item.hora_salida))
		) {
			setError("Hay fichajes seleccionados sin una hora de salida valida.");
			return;
		}

		setSaving(true);
		setError("");
		setMessage("");

		try {
			const response = await guardarPreparacionExportacionFichajes({
				action,
				items: payloadItems,
			});

			if (response?.success) {
				await cargarDatos({ preserveMessage: true });
				setMessage(response.message || "Preparacion actualizada.");
			} else {
				setError(response?.message || "No se pudo guardar la preparacion.");
			}
		} catch (err) {
			console.error("Error guardando preparacion:", err);
			setError("No se pudo guardar la preparacion de exportacion.");
		} finally {
			setSaving(false);
		}
	};

	return (
		<Grid container direction="column" size={{ xs: 12 }}>
			<Grid item size={{ xs: 12 }}>
				<SubHeader titulo="Preparar exportacion de fichajes" />
			</Grid>

			<Grid container size={{ xs: 12 }} sx={{ px: 2, pb: 2 }}>
				<Button
					variant="text"
					startIcon={<ArrowBackIcon />}
					onClick={() => navigate("/admin")}
				>
					Volver a administracion
				</Button>
			</Grid>

			<Grid container spacing={2} sx={{ px: 2, pb: 2 }}>
				<Grid item size={{ xs: 12, md: 2 }}>
					<TextField
						label="Fecha desde"
						type="date"
						size="small"
						fullWidth
						value={fechaDesde}
						onChange={(event) => setFechaDesde(event.target.value)}
						InputLabelProps={{ shrink: true }}
					/>
				</Grid>

				<Grid item size={{ xs: 12, md: 2 }}>
					<TextField
						label="Fecha hasta"
						type="date"
						size="small"
						fullWidth
						value={fechaHasta}
						onChange={(event) => setFechaHasta(event.target.value)}
						InputLabelProps={{ shrink: true }}
					/>
				</Grid>

				<Grid item size={{ xs: 12, md: 2 }}>
					<TextField
						label="Hora base"
						type="time"
						size="small"
						fullWidth
						value={horaBase}
						onChange={(event) => setHoraBase(normalizeTimeValue(event.target.value))}
						InputLabelProps={{ shrink: true }}
						inputProps={{ step: 60 }}
					/>
				</Grid>

				<Grid item size={{ xs: 12, md: 3 }}>
					<TextField
						label="Buscar"
						size="small"
						fullWidth
						value={search}
						onChange={(event) => setSearch(event.target.value)}
						placeholder="Persona, NIF, cliente, cabo o cuadrilla"
					/>
				</Grid>

				<Grid item size={{ xs: 12, md: 3 }}>
					<Box
						sx={{
							border: "1px solid",
							borderColor: "divider",
							borderRadius: 1,
							px: 1.5,
							py: 0.75,
						}}
					>
						<Typography variant="caption" color="text.secondary">
							Filtros de estado
						</Typography>

						<FormGroup row>
							<FormControlLabel
								control={
									<Checkbox
										size="small"
										checked={filtrosEstado.validado}
										onChange={() => handleFiltroEstadoChange("validado")}
									/>
								}
								label="Validado"
							/>

							<FormControlLabel
								control={
									<Checkbox
										size="small"
										checked={filtrosEstado.revisado}
										onChange={() => handleFiltroEstadoChange("revisado")}
									/>
								}
								label="Revisado"
							/>
						</FormGroup>
					</Box>
				</Grid>

				<Grid item size={{ xs: 12, md: 3 }}>
					<Box
						sx={{
							border: "1px solid",
							borderColor: "divider",
							borderRadius: 1,
							px: 1.5,
							py: 0.75,
						}}
					>
						<Typography variant="caption" color="text.secondary">
							Filtro de preparación
						</Typography>

						<FormGroup row>
							<FormControlLabel
								control={
									<Checkbox
										size="small"
										checked={filtrosPreparacion.pendientes}
										onChange={() => handleFiltroPreparacionChange("pendientes")}
									/>
								}
								label="Pendientes"
							/>

							<FormControlLabel
								control={
									<Checkbox
										size="small"
										checked={filtrosPreparacion.preparados}
										onChange={() => handleFiltroPreparacionChange("preparados")}
									/>
								}
								label="Exportados"
							/>
						</FormGroup>
					</Box>
				</Grid>

				<Grid
					item
					size={{ xs: 12, md: 5 }}
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 1,
						flexWrap: "wrap",
					}}
				>
					<Button
						variant="contained"
						onClick={() => cargarDatos()}
						disabled={saving}
						startIcon={<RefreshIcon />}
					>
						Buscar
					</Button>

					<Button
						variant="outlined"
						onClick={() => setOpenConvenios(true)}
						disabled={saving || accessDenied}
						startIcon={<WorkHistoryIcon />}
					>
						Gestionar convenios
					</Button>

					<Typography variant="caption" color="text.secondary">
						Carga todos por defecto
					</Typography>
				</Grid>
			</Grid>

			<Grid container spacing={2} sx={{ px: 2, pb: 2 }}>
				<Grid item size={{ xs: 12, sm: 6, md: 3 }}>
					<Paper variant="outlined" sx={{ p: 2 }}>
						<Typography variant="caption" color="text.secondary">
							Total visible
						</Typography>
						<Typography variant="h5">{resumenVisible.total}</Typography>
						<Typography variant="caption" color="text.secondary">
							Total cargado: {resumen.total}
						</Typography>
					</Paper>
				</Grid>

				<Grid item size={{ xs: 12, sm: 6, md: 3 }}>
					<Paper variant="outlined" sx={{ p: 2 }}>
						<Typography variant="caption" color="text.secondary">
							Pendientes visibles
						</Typography>
						<Typography variant="h5">{resumenVisible.pendientes}</Typography>
						<Typography variant="caption" color="text.secondary">
							Pendientes cargados: {resumen.pendientes}
						</Typography>
					</Paper>
				</Grid>

				<Grid item size={{ xs: 12, sm: 6, md: 3 }}>
					<Paper variant="outlined" sx={{ p: 2 }}>
						<Typography variant="caption" color="text.secondary">
							Exportados visibles
						</Typography>

						<Typography variant="h5">
							{resumenVisible.preparados}/{resumenVisible.total}
						</Typography>

						<Typography variant="caption" color="text.secondary">
							{resumenVisible.total > 0
								? `${Math.round(
										(resumenVisible.preparados / resumenVisible.total) * 100
								  )}% exportado`
								: "0% exportado"}
						</Typography>
					</Paper>
				</Grid>

				<Grid item size={{ xs: 12, sm: 6, md: 3 }}>
					<Paper variant="outlined" sx={{ p: 2 }}>
						<Typography variant="caption" color="text.secondary">
							Cuadrillas visibles
						</Typography>
						<Typography variant="h5">{resumenVisible.cuadrillas}</Typography>
						<Typography variant="caption" color="text.secondary">
							Cuadrillas cargadas: {resumen.cuadrillas}
						</Typography>
					</Paper>
				</Grid>
			</Grid>

			<Grid container spacing={1} sx={{ px: 2, pb: 2 }} alignItems="center">
				<Button
					variant="outlined"
					size="small"
					onClick={toggleAllVisible}
					disabled={!visibleRows.length || saving}
					startIcon={<DoneAllIcon />}
				>
					{allVisibleSelected ? "Deseleccionar visibles" : "Seleccionar visibles"}
				</Button>

				<Button
					variant="outlined"
					size="small"
					onClick={seleccionarPendientes}
					disabled={!visibleRows.length || saving}
					startIcon={<PlaylistAddCheckIcon />}
				>
					Seleccionar pendientes
				</Button>

				<Button
					variant="outlined"
					size="small"
					onClick={limpiarSeleccion}
					disabled={!selectedCount || saving}
					startIcon={<BackspaceIcon />}
				>
					Limpiar seleccion
				</Button>

				<Button
					variant="outlined"
					size="small"
					onClick={aplicarHoraBase}
					disabled={!selectedCount || saving}
					startIcon={<ScheduleIcon />}
				>
					Aplicar hora base
				</Button>

				<Button
					variant="contained"
					size="small"
					onClick={() => guardarSeleccion("prepare")}
					disabled={!selectedCount || saving || accessDenied}
					startIcon={<FileUploadIcon />}
				>
					EXPORTAR SELECCIONADOS
				</Button>

				<Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
					Seleccionados visibles: {selectedCount}
				</Typography>
			</Grid>

			{!!message && (
				<Grid sx={{ px: 2, pb: 2 }}>
					<Alert severity="success">{message}</Alert>
				</Grid>
			)}

			{!!error && (
				<Grid sx={{ px: 2, pb: 2 }}>
					<Alert severity={accessDenied ? "warning" : "error"}>{error}</Alert>
				</Grid>
			)}

			<Grid sx={{ px: 2, pb: 3 }}>
				<Paper variant="outlined">
					{!loaded ? (
						<Box
							sx={{
								minHeight: "40vh",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								gap: 1,
							}}
						>
							<CircularProgress size={20} />
							<Typography variant="body2">Cargando fichajes...</Typography>
						</Box>
					) : visibleRows.length === 0 ? (
						<Box sx={{ p: 3 }}>
							<Alert severity="info">
								No hay fichajes para el rango y filtro seleccionados.
							</Alert>
						</Box>
					) : (
						<Box>
							{groupedRows.map((group) => {
								const cuadrillaId = group.cuadrilla.id;
								const allInGroupSelected = group.fichajes.every(
									(item) => selectedRows[rowKeyFromItem(item)]
								);
								const totalFichajes = group.fichajes.length;
								const preparados = group.fichajes.filter(
									(item) => Number(item?.exportado ?? 0) === 1
								).length;
								const pendientes = totalFichajes - preparados;

								return (
									<Accordion key={cuadrillaId}>
										<AccordionSummary expandIcon={<ExpandMoreIcon />}>
											<Box sx={{ width: "100%" }}>
												<Box
													sx={{
														display: "flex",
														alignItems: "center",
														gap: 1,
														mb: 1,
													}}
												>
													<Typography variant="h6" sx={{ fontWeight: 600 }}>
														{group.cuadrilla.nombre || `Cuadrilla ${cuadrillaId}`}
													</Typography>
												</Box>

												<Box
													sx={{
														display: "flex",
														alignItems: "center",
														gap: 2,
														width: "100%",
														flexWrap: "wrap",
													}}
												>
													<Button
														size="small"
														variant="outlined"
														onClick={(e) => {
															e.stopPropagation();
															toggleAllInGroup(group);
														}}
														disabled={saving}
													>
														{allInGroupSelected
															? "Deseleccionar todos"
															: "Seleccionar todos"}
													</Button>

													<Chip
														label={`${totalFichajes} fichaje${
															totalFichajes !== 1 ? "s" : ""
														}`}
														size="small"
														color="primary"
														variant="outlined"
													/>

													<Chip
														label={`${preparados}/${totalFichajes} exportados`}
														size="small"
														color={
															preparados === totalFichajes
																? "success"
																: preparados > 0
																? "warning"
																: "default"
														}
														variant={
															preparados === totalFichajes ? "filled" : "outlined"
														}
													/>

													<Typography variant="body2" color="text.secondary">
														Pendientes: {pendientes}
													</Typography>

													<Typography variant="body2" color="text.secondary">
														Cliente: {group.cuadrilla.cliente || "-"}
													</Typography>

													<Typography variant="body2" color="text.secondary">
														Fecha:{" "}
														{formatDateTimeEs(group.cuadrilla.fecha, {
															includeTime: false,
														})}
													</Typography>

													<Box
														sx={{ minWidth: 260 }}
														onClick={(e) => e.stopPropagation()}
													>
														<FormControl fullWidth size="small">
															<InputLabel id={`convenio-parte-${cuadrillaId}`}>
																Convenio
															</InputLabel>

															<Select
																labelId={`convenio-parte-${cuadrillaId}`}
																label="Convenio"
																value={
																	convenioByGroup[cuadrillaId] !== undefined
																		? convenioByGroup[cuadrillaId]
																		: group.cuadrilla.id_convenio
																		? String(group.cuadrilla.id_convenio)
																		: ""
																}
																onChange={(event) =>
																	handleConvenioParteChange(
																		cuadrillaId,
																		event.target.value
																	)
																}
																disabled={
																	saving ||
																	conveniosLoading ||
																	Boolean(savingConvenioByGroup[cuadrillaId])
																}
															>
																<MenuItem value="">Sin convenio</MenuItem>

																{convenios
																	.filter(
																		(convenio) =>
																			Number(convenio.activo ?? 1) === 1
																	)
																	.map((convenio) => (
																		<MenuItem
																			key={convenio.id}
																			value={String(convenio.id)}
																		>
																			{convenio.nombre} -{" "}
																			{Number(
																				convenio.horas_convenio
																			).toFixed(2)}{" "}
																			h
																		</MenuItem>
																	))}
															</Select>
														</FormControl>
													</Box>

													<Box sx={{ flex: 1, minWidth: 120 }} />

													<Box
														sx={{
															display: "flex",
															gap: 1,
															alignItems: "center",
														}}
														onClick={(e) => e.stopPropagation()}
													>
														<TextField
															label="Hora base"
															type="time"
															size="small"
															value={normalizeTimeValue(
																horaBaseByGroup[cuadrillaId] || horaBase
															)}
															onChange={(event) =>
																setHoraBaseByGroup((current) => ({
																	...current,
																	[cuadrillaId]: normalizeTimeValue(
																		event.target.value
																	),
																}))
															}
															InputLabelProps={{ shrink: true }}
															inputProps={{ step: 60 }}
															disabled={saving}
															sx={{ width: 140 }}
														/>

														<Button
															size="small"
															variant="outlined"
															onClick={(e) => {
																e.stopPropagation();
																applyHourBaseToGroup(group);
															}}
															disabled={saving}
														>
															Aplicar
														</Button>
													</Box>
												</Box>
											</Box>
										</AccordionSummary>

										<AccordionDetails>
											<Table sx={{ tableLayout: "fixed" }}>
												<TableHead>
													<TableRow>
														<TableCell sx={{ width: "60px" }}>
															Seleccionar
														</TableCell>
														<TableCell sx={{ width: "150px" }}>Usuario</TableCell>
														<TableCell sx={{ width: "130px" }}>
															Hora Entrada
														</TableCell>
														<TableCell sx={{ width: "130px" }}>
															Hora Salida
														</TableCell>
														<TableCell sx={{ width: "100px" }}>NIF</TableCell>
														<TableCell sx={{ width: "80px" }}>Empleo</TableCell>
														<TableCell sx={{ width: "120px" }}>Similitud</TableCell>
														<TableCell sx={{ width: "110px" }}>Estado</TableCell>
														<TableCell sx={{ width: "140px" }}>
															Fecha Exportado
														</TableCell>
													</TableRow>
												</TableHead>

												<TableBody>
													{group.fichajes.map((item) => {
														const key = rowKeyFromItem(item);
														const prepared = Number(item?.exportado ?? 0) === 1;

														const horaEntrada =
															normalizeTimeValue(hoursByRow[key]) ||
															normalizeTimeValue(item?.hora_entrada_preparada) ||
															horaBase;

														const horaSalida =
															normalizeTimeValue(exitHoursByRow[key]) ||
															normalizeTimeValue(item?.hora_salida_preparada) ||
															addHoursToTime(horaEntrada, item?.horas_convenio);

														return (
															<TableRow
																key={key}
																onClick={() => toggleRow(item)}
																sx={{ cursor: "pointer" }}
															>
																<TableCell>
																	<Checkbox
																		checked={Boolean(selectedRows[key])}
																		onChange={(e) => {
																			e.stopPropagation();
																			toggleRow(item);
																		}}
																		size="small"
																	/>
																</TableCell>

																<TableCell>
																	<Box
																		sx={{
																			overflow: "hidden",
																			textOverflow: "ellipsis",
																			whiteSpace: "nowrap",
																		}}
																	>
																		{item.nombre_completo || "-"}
																	</Box>
																</TableCell>

																<TableCell>
																	<TextField
																		label="Hora entrada"
																		type="time"
																		size="small"
																		value={horaEntrada}
																		onChange={(event) =>
																			handleHoraEntradaChange(
																				item,
																				event.target.value
																			)
																		}
																		InputLabelProps={{ shrink: true }}
																		inputProps={{ step: 60 }}
																		disabled={saving}
																		onClick={(e) => e.stopPropagation()}
																	/>
																</TableCell>

																<TableCell>
																	<TextField
																		label="Hora salida"
																		type="time"
																		size="small"
																		value={horaSalida}
																		onChange={(event) =>
																			handleHoraSalidaChange(
																				item,
																				event.target.value
																			)
																		}
																		InputLabelProps={{ shrink: true }}
																		inputProps={{ step: 60 }}
																		disabled={saving}
																		onClick={(e) => e.stopPropagation()}
																	/>
																</TableCell>

																<TableCell>{item.nif_recolector || "-"}</TableCell>
																<TableCell>{item.id_empleo || "-"}</TableCell>

																<TableCell>
																	{item?.puntuacion_comprobacion !== undefined &&
																	item?.puntuacion_comprobacion !== null ? (
																		<Chip
																			size="small"
																			variant="outlined"
																			label={`Similitud: ${formatScoreOverHundred(
																				item.puntuacion_comprobacion
																			)}`}
																		/>
																	) : (
																		"-"
																	)}
																</TableCell>

																<TableCell>
																	<Chip
																		size="small"
																		color={getEstadoColor(item)}
																		label={prepared ? "Preparado" : "Pendiente"}
																	/>
																</TableCell>

																<TableCell>
																	{item.fecha_exportado_in
																		? formatDateTimeEs(item.fecha_exportado_in)
																		: "-"}
																</TableCell>
															</TableRow>
														);
													})}
												</TableBody>
											</Table>
										</AccordionDetails>
									</Accordion>
								);
							})}
						</Box>
					)}
				</Paper>
			</Grid>

			<Convenios
				open={openConvenios}
				onClose={() => setOpenConvenios(false)}
				dialogMode
				onConvenioSaved={() => {
					cargarConvenios();
					cargarDatos({ preserveMessage: true });
				}}
			/>
		</Grid>
	);
};